import { Col } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { flushBuffer, trackStructEvent } from "@snowplow/browser-tracker";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { BLOCKING_ERROR_TYPE } from "@/apis/constants";
import { Logger } from "@/apis/Logger";
import { JourneyConsentScreen } from "@/components/AIAutomation";
import {
  type JourneyConsentState,
  isStepThatAsks,
  resolveJourneyConsent,
  shouldAskJourneyConsent,
} from "@/components/AIAutomation/journey-consent-gate";
import AllStepsCompleted from "@/components/AllStepsCompleted";
import AmazonAccountCaching from "@/components/AmazonAccountCaching";
import AmazonPassword from "@/components/AmazonPassword";
import AmazonUsername from "@/components/AmazonUsername";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { AppNotAvailableError } from "@/components/Commons/ErrorBanner/AppNotAvailableError";
import { IneligibleError } from "@/components/Commons/ErrorBanner/IneligibleError";
import StepContainer from "@/components/Commons/StepContainer";
import ConnectVpn from "@/components/ConnectVpn";
import { Step, STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { getRefPageByError } from "@/components/GetSupport/ChatWidget/helper";
import IdentityCheck from "@/components/IdentityCheck";
import InsertAndTestSecurityKey from "@/components/InsertAndTestSecurityKey";
import { MacOsPermissionsScreen } from "@/components/MacOsPermissions/MacOsPermissionsScreen";
import { shouldAskMacOsPermissions } from "@/components/MacOsPermissions/permissions-gate";
import PreCacheADMobile from "@/components/PreCacheADMobile";
import RegisterSecurityKey from "@/components/RegisterSecurityKey";
import { setStepCache } from "@/helpers/CachedStepProgress";
import { isSessionObserveAvailable } from "@/helpers/electron-helper";
import { SnowplowTracker } from "@/helpers/snowplow";
import { useCheckReqs } from "@/hooks/useCheckReqs";
import {
  useIsAiOfferable,
  useIsJourneyCaptureAvailable,
} from "@/hooks/useIsJourneyCaptureAvailable";
import { useLoadRegion } from "@/hooks/useLoadRegion";
import { AppContext } from "@/stores/appStore";
import { ActionType, getEnabledSteps } from "@/stores/constants";
import { useFeatureFlagsContext } from "@/stores/contexts/FeatureFlagsContext";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export const STEP_COMPONENT_MAP: Record<STEP_ID, React.ComponentType> = {
  [STEP_ID.IDENTITY_CHECK]: IdentityCheck,
  [STEP_ID.AMAZON_USERNAME]: AmazonUsername,
  [STEP_ID.INSERT_AND_TEST_SECURITY_KEY]: InsertAndTestSecurityKey,
  [STEP_ID.REGISTER_SECURITY_KEY]: RegisterSecurityKey,
  [STEP_ID.CONNECT_VPN]: ConnectVpn,
  [STEP_ID.AMAZON_PASSWORD]: AmazonPassword,
  [STEP_ID.PRE_CACHE_AD_MOBILE]: PreCacheADMobile,
  [STEP_ID.AMAZON_ACCOUNT_CACHING]: AmazonAccountCaching,
};

export interface HomeContentProps {
  /** The session's answer to the capture consent question. */
  readonly journeyConsent: JourneyConsentState;
  /** Called once an answer is on record, so the host re-reads it. */
  readonly onJourneyConsentAnswered: () => void;
  /**
   * Reports whether any takeover is on screen -- the consent question or the permissions
   * walkthrough -- so the page can drop the progress rail. Owned here because only this component
   * knows which of its screens is showing.
   */
  readonly onTakeoverChange: (isShowingTakeover: boolean) => void;
}

export default function HomeContent({
  journeyConsent,
  onJourneyConsentAnswered,
  onTakeoverChange,
}: HomeContentProps): JSX.Element {
  const intl = useIntl();
  const { featureFlags, isLoading: isFlagsLoading } = useFeatureFlagsContext();
  const isJourneyCaptureAvailable = useIsJourneyCaptureAvailable();
  const isAiOfferable = useIsAiOfferable();
  const mayOfferCapture = isJourneyCaptureAvailable && isAiOfferable;

  /**
   * Renders a single onboarding step, or nothing when no component is mapped to it.
   *
   * @param step The step to render.
   */
  const renderStep = (step: Step) => {
    const StepContent = STEP_COMPONENT_MAP[step.id];

    if (!StepContent) {
      void Logger.error(
        `Error in loading the step content since the ${step.id} component not set`
      );
      return null;
    }

    return (
      <StepContainer key={step.id} stepId={step.id}>
        <StepContent></StepContent>
      </StepContainer>
    );
  };

  /**
   * One step per page: only the step the new hire is working on is rendered. Once every
   * step is complete currentStepNum points past the last step, so there is no active step
   * to render and AllStepsCompleted takes over.
   *
   * Derived from `state.steps` rather than the full step list so this resolves against the
   * same list ProgressRail renders. `state.steps` is rebuilt with the feature flags by the
   * REBUILD_STEPS reducer case, which runs from an effect, so for one render after the
   * flags arrive it still holds the pre-flag list. Deriving from the full list instead
   * would disagree with the rail for that render, shifting every index after a gated step.
   */
  const onboardingSteps = () => {
    const activeStep = getEnabledSteps(state.steps, featureFlags)[
      state.currentStepNum - 1
    ];

    return activeStep ? renderStep(activeStep) : null;
  };

  /** Identity the healthy region and init Amplify configuration */
  const { isError: isLoadRegionError } = useLoadRegion({});

  const {
    isFetched: isCheckReqsFetched,
    isError: isCheckReqsError,
    candidateLocalStartDate,
    blockingErrorType,
  } = useCheckReqs();
  const { state, dispatch } = useContext(AppContext);
  const allStepsCompleted = getEnabledSteps(state.steps, featureFlags).every(
    (step) => step.completed
  );

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support. When a blocking error of any kind occurs, override the
   * step-default refPage (e.g. "ezo-identity-check") with one that reflects the
   * actual screen the new hire is seeing — otherwise chat support and our logs
   * mislabel a blocking-error session as if the user were on the identity-check
   * step.
   */
  useEffect(() => {
    if (!blockingErrorType) {
      return;
    }
    if (
      blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
      !candidateLocalStartDate
    ) {
      // BeforeDay1 without a start date can't render the dedicated banner;
      // wait until candidateLocalStartDate is set before dispatching to keep
      // existing behavior for that branch.
      return;
    }
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: getRefPageByError(blockingErrorType),
    });
  }, [blockingErrorType, candidateLocalStartDate]);

  useEffect(() => {
    if (allStepsCompleted) {
      trackStructEvent({
        category: Day1EventCategory.Complete,
        action: Day1EventAction.SectionToggled,
      });
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: "ON_WIZARD_COMPLETED_STEP",
      });
      // Force all buffered events to be flushed since this is the end of the app flow.
      // Otherwise buffered events may be lost.
      flushBuffer();
    }
  }, [allStepsCompleted]);

  // Keep the cache up to date when the wizard advances.
  useEffect(() => {
    setStepCache(state.currentStepNum - 1);
  }, [state.currentStepNum]);

  /**
   * Record the per step "section opened" journey metric when the wizard advances.
   *
   * This lives here rather than in the step itself because HomeContent stays mounted while
   * the active step is swapped out, so the previous step number survives the transition.
   * Landing on a step is not an advance, so nothing is recorded on the first run and a
   * fresh page load or a resume from cached progress does not look like a transition.
   */
  const previousStepNum = useRef<number | null>(null);
  const currentStepEventCategory = state.currentStep?.eventCategory;
  useEffect(() => {
    const hasAdvanced =
      previousStepNum.current !== null &&
      previousStepNum.current !== state.currentStepNum;

    if (hasAdvanced && currentStepEventCategory) {
      trackStructEvent({
        category: currentStepEventCategory,
        action: Day1EventAction.SectionToggled,
      });
    }

    previousStepNum.current = state.currentStepNum;
  }, [state.currentStepNum, currentStepEventCategory]);

  useEffect(() => {
    if (isCheckReqsFetched) {
      SnowplowTracker.trackSystemInfo();
    }
  }, [isCheckReqsFetched]);

  /*
   * True a commit after the readiness checks land, because the blocking-error classification is
   * applied in an effect: asked any sooner, an ineligible hire is put the question for a frame.
   */
  const [isBlockingErrorClassified, setIsBlockingErrorClassified] =
    useState(false);
  useEffect(() => {
    if (isCheckReqsFetched) {
      setIsBlockingErrorClassified(true);
    }
  }, [isCheckReqsFetched]);

  /*
   * Every condition the returns below apply is folded in here, because the page reflects this
   * same answer when deciding whether to show the rail. Derived twice, the two disagreed.
   */
  const hasBlockingError =
    (blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
      Boolean(candidateLocalStartDate)) ||
    isLoadRegionError ||
    isCheckReqsError;

  /*
   * Re-resolved when the page's copy still says "ask": the record only becomes readable once a
   * person resolves, which can happen in the very commit that opens this gate.
   */
  const resolvedConsent =
    journeyConsent.kind === "ask" ? resolveJourneyConsent() : journeyConsent;

  const isReadyToRenderStep =
    isCheckReqsFetched && !isFlagsLoading && isBlockingErrorClassified;

  /*
   * Session-scoped on purpose. A grant can be revoked at any time, so the walkthrough is offered
   * again on a fresh load rather than remembered as permanently done.
   */
  const [hasAnsweredPermissions, setHasAnsweredPermissions] = useState(false);

  const isAskingJourneyConsent =
    !hasBlockingError &&
    isReadyToRenderStep &&
    shouldAskJourneyConsent(
      mayOfferCapture,
      resolvedConsent,
      state.currentStepNum
    );

  /*
   * Withheld while readiness resolves, or the rail appears beside the spinner and then vanishes as the
   * consent screen takes over. The rollout flag cannot be consulted this early and `mayOfferCapture`
   * needs a hiring region no request has returned, so the accepted cost is that a recording build
   * outside the rollout is briefly railless on the first step.
   */
  const isAwaitingPossibleConsent =
    !hasBlockingError &&
    !isReadyToRenderStep &&
    isStepThatAsks(state.currentStepNum) &&
    isSessionObserveAvailable();

  /*
   * No region term, unlike the consent gate: requiring a settled region would withhold this from a
   * returning hire whose identity submission is still pending. Session start is region-guarded in
   * useObserveSession instead.
   */
  const isAskingMacOsPermissions =
    !hasBlockingError &&
    isReadyToRenderStep &&
    !isAskingJourneyConsent &&
    shouldAskMacOsPermissions(
      isJourneyCaptureAvailable,
      resolvedConsent,
      state.currentStepNum,
      hasAnsweredPermissions
    );

  /*
   * The rail comes down for the permissions walkthrough too. It marks the step the hire is on, and on
   * a takeover that is not the wizard it names the wrong one -- the design shows neither screen with it.
   */
  useLayoutEffect(() => {
    onTakeoverChange(
      isAskingJourneyConsent ||
        isAwaitingPossibleConsent ||
        isAskingMacOsPermissions
    );
  }, [
    isAskingJourneyConsent,
    isAskingMacOsPermissions,
    isAwaitingPossibleConsent,
    onTakeoverChange,
  ]);

  if (
    blockingErrorType === BLOCKING_ERROR_TYPE.BeforeDay1 &&
    candidateLocalStartDate
  ) {
    return (
      <ErrorBanner>
        <FormattedMessage
          id="error-beforeStartDate"
          values={{
            chatWithITSupportLink: (
              <ChatWithITSupportFlyoutLink
                localeMessageId="text-chatWithITSupport"
                metricProps={{
                  category: Day1EventCategory.BlockingError,
                  action: Day1EventAction.ChatWithSupport,
                  label: Day1EventLabel.NotDay1,
                }}
              />
            ),
            startDate: intl.formatDate(candidateLocalStartDate.toDate(), {
              month: "long",
              day: "numeric",
            }),
          }}
        />
      </ErrorBanner>
    );
  }

  /**
   * Failed to resolve a healthy EZO API region, so EZO is unavailable.
   * Direct the new hire to use First Aid and engage their manager to
   * onboard with virtual new hire orientation (vNHO)
   */
  if (isLoadRegionError) {
    return <AppNotAvailableError />;
  }

  if (isCheckReqsError) {
    return <IneligibleError />;
  }

  if (isReadyToRenderStep) {
    if (isAskingJourneyConsent) {
      return <JourneyConsentScreen onAnswered={onJourneyConsentAnswered} />;
    }

    /*
     * After consent, before anything the assistant does. Both are takeover screens on the step that
     * is never captured, so the answer and the grants are settled before a frame can be recorded.
     */
    if (isAskingMacOsPermissions) {
      return (
        <MacOsPermissionsScreen
          onContinue={() => setHasAnsweredPermissions(true)}
          onGiveUp={() => setHasAnsweredPermissions(true)}
        />
      );
    }

    return (
      <Col gridGap="S400">
        {onboardingSteps()}
        {allStepsCompleted && <AllStepsCompleted />}
      </Col>
    );
  }

  return (
    <Spinner
      dataTestId="homeContentSpinner"
      showText
      size={SpinnerSize.Medium}
    />
  );
}
